import type { LocaleId } from "@dawn/i18n";
import { isStagingDeploy } from "./deployTarget";

export interface ChangelogRelease {
  build: number;
  timestamp: string;
  descriptions: Partial<Record<LocaleId, string>>;
  gitCommit?: string | null;
}

export interface ChangelogData {
  releases: ChangelogRelease[];
}

const CHANGELOG_URL = "/changelog.json";

export function formatBuildLabel(build: number): string {
  const label = `Build ${build}`;
  return isStagingDeploy() ? `${label} · Staging` : label;
}

export function getReleaseDescription(
  release: ChangelogRelease,
  locale: LocaleId,
): string {
  return release.descriptions[locale] ?? release.descriptions.en ?? "";
}

export function getReleaseId(release: ChangelogRelease): string {
  return String(release.build);
}

export function sortReleases(releases: ChangelogRelease[]): ChangelogRelease[] {
  return [...releases].sort((a, b) => {
    if (a.build !== b.build) {
      return b.build - a.build;
    }
    return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
  });
}

export function getLatestRelease(data: ChangelogData): ChangelogRelease | undefined {
  return sortReleases(data.releases)[0];
}

export async function fetchChangelog(): Promise<ChangelogData | null> {
  try {
    const response = await fetch(CHANGELOG_URL);
    if (!response.ok) {
      return null;
    }
    return (await response.json()) as ChangelogData;
  } catch {
    return null;
  }
}
